// Recipe images live in a Wix media library and the stored URLs point at the
// untouched originals: 2500-3750px PNGs of 5-19 MB each. Wix's CDN resizes on
// the fly when a transform is added to the path, so instead of storing copies
// we ask for an image sized to the box it is shown in. `enc_auto` lets Wix pick
// AVIF or WebP from the browser's Accept header.
//
//   https://static.wixstatic.com/media/<file>/v1/fill/w_480,h_360,al_c,q_80,enc_auto/<file>
//
// Any URL that is not a plain Wix media URL is passed through untouched.

const WIX_MEDIA = /^(https:\/\/static\.wixstatic\.com\/media\/([^/?#]+))(?:\/v1\/[^?#]*)?$/;

const QUALITY = 80;

/** A Wix URL for a centre-cropped copy at exactly width x height, or null if not a Wix media URL. */
export function wixFillUrl(url: string, width: number, height: number): string | null {
    const match = WIX_MEDIA.exec(url.trim());
    if (!match) return null;
    const [, base, file] = match;
    const w = Math.round(width);
    const h = Math.round(height);
    return `${base}/v1/fill/w_${w},h_${h},al_c,q_${QUALITY},enc_auto/${file}`;
}

export type RecipeImageVariant = 'card' | 'hero' | 'thumb';

interface VariantSpec {
    /** Width over height of the requested crop. */
    aspect: number;
    /** Candidate widths for srcset, smallest first. */
    widths: number[];
    sizes: string;
}

// Grid card: aspect-4/3 box, one column on phones, two from sm, a 288px filter
// sidebar from lg, three columns from xl inside max-w-7xl (about 290px each).
const CARD: VariantSpec = {
    aspect: 4 / 3,
    widths: [320, 480, 640, 800, 1080],
    sizes: '(min-width: 1280px) 320px, (min-width: 1024px) calc(50vw - 200px), (min-width: 640px) calc(50vw - 40px), calc(100vw - 32px)',
};

// Detail hero: full width, 256px tall on phones, 320px from md, 384px from lg.
// A 2:1 crop covers every one of those boxes. On a phone the 256px height is
// the constraint, so the image is drawn 512px wide and cropped at the sides.
const HERO: VariantSpec = {
    aspect: 2,
    widths: [640, 960, 1280, 1600, 1920],
    sizes: '(min-width: 768px) 100vw, 512px',
};

/** Fixed-size thumbnails (list rows, menus, pickers). */
function thumbSpec(size: number, aspect: number): VariantSpec {
    return { aspect, widths: [1, 2, 3].map((x) => Math.round(size * x)), sizes: `${size}px` };
}

export interface RecipeImageAttrs {
    src: string;
    srcSet?: string;
    sizes?: string;
    width: number;
    height: number;
}

/**
 * The src/srcSet/sizes/width/height for a recipe image in a given slot.
 * For `thumb`, pass the rendered CSS width (and aspect if not square).
 */
export function recipeImageAttrs(
    url: string,
    variant: RecipeImageVariant,
    thumb: { size: number; aspect?: number } = { size: 80 },
): RecipeImageAttrs {
    const spec =
        variant === 'card'
            ? CARD
            : variant === 'hero'
              ? HERO
              : thumbSpec(thumb.size, thumb.aspect ?? 1);
    const largest = spec.widths[spec.widths.length - 1];
    // width/height only give the browser the aspect ratio before the image arrives.
    const width = spec.widths[0];
    const height = Math.round(width / spec.aspect);

    const at = (w: number) => wixFillUrl(url, w, w / spec.aspect);
    if (at(largest) === null) return { src: url, width, height };

    return {
        // Fallback for browsers without srcset support: a mid-sized candidate.
        src: at(spec.widths[Math.min(2, spec.widths.length - 1)])!,
        srcSet: spec.widths.map((w) => `${at(w)} ${w}w`).join(', '),
        sizes: spec.sizes,
        width,
        height,
    };
}
